/**
 * Glass surface helpers for FolioWorth.
 *
 * These emit `var(--token)` references rather than literal colours, so
 * `tokens.css` stays the single source of truth and theme switching is a
 * single `data-theme` attribute flip with no React re-render of colour values.
 *
 * Tiering, from furthest to closest to the viewer:
 *   tile    -> nested inside a panel; tint only, NO blur (blur-on-blur is muddy)
 *   surface -> panels and cards in the content flow
 *   chrome  -> app bar / sidebar, sits over scrolling content
 *   overlay -> dialogs, menus, tooltips
 */

/** Shared blur+saturate filter string. */
const filter = (strong = false) =>
  `blur(var(${strong ? '--glass-blur-strong' : '--glass-blur'})) saturate(var(--glass-saturate))`;

export type Tier = 'surface' | 'chrome' | 'overlay';

interface GlassOptions {
  tier?: Tier;
  /** Which of the three veil steps to use. */
  level?: 1 | 2 | 3;
  border?: boolean;
  /** Emit the inner top highlight that reads as a lit glass edge. */
  sheen?: boolean;
}

/**
 * Build a frosted-glass surface.
 *
 * Args:
 *   options: Tier, veil level, border and sheen toggles.
 *
 * Returns:
 *   A style object for an MUI `styleOverrides` slot or an `sx` prop.
 */
export const glass = (options: GlassOptions = {}) => {
  const { tier = 'surface', level = 2, border = true, sheen = true } = options;
  const strong = tier !== 'surface';
  const f = filter(strong);

  return {
    background: `var(--glass-${level})`,
    backdropFilter: f,
    WebkitBackdropFilter: f,
    backgroundImage: 'none',
    ...(border ? { border: '1px solid var(--border)' } : {}),
    boxShadow: sheen
      ? `var(--glass-sheen), var(--shadow-${tier === 'overlay' ? 'xl' : 'md'})`
      : `var(--shadow-${tier === 'overlay' ? 'xl' : 'md'})`,
    // Reason: without backdrop-filter the low veil alpha leaves text on a
    // near-transparent panel, so fall back to a fully opaque surface.
    '@supports not ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px)))': {
      background: 'var(--surface-solid)',
    },
  };
};

/**
 * Tint for a tile nested inside a glass panel.
 *
 * No backdrop-filter on purpose: stacking blur on blur costs a second
 * offscreen composite per element and reads as muddy grey.
 */
export const glassTile = () => ({
  background: 'var(--glass-1)',
  border: '1px solid var(--border-soft)',
  borderRadius: 'var(--radius-md)',
  backgroundImage: 'none',
});

/** Hover lift for interactive glass. Never changes layout size. */
export const glassInteractive = () => ({
  cursor: 'pointer',
  transition:
    'background-color var(--dur-base) var(--ease-out), border-color var(--dur-base) var(--ease-out), box-shadow var(--dur-base) var(--ease-out), transform var(--dur-base) var(--ease-out)',
  '&:hover': {
    background: 'var(--glass-hover)',
    borderColor: 'var(--border-strong)',
    boxShadow: 'var(--glass-sheen), var(--shadow-lg)',
    transform: 'translateY(-2px)',
  },
  '&:active': {
    background: 'var(--glass-active)',
    transform: 'translateY(0)',
  },
  '@media (prefers-reduced-motion: reduce)': {
    transition: 'none',
    '&:hover': { transform: 'none' },
  },
});

/**
 * Literal colour values mirroring `tokens.css`.
 *
 * MUI's palette needs real colours (it computes contrast text and alpha
 * ramps, which it cannot do through `var()`). Keep these in sync with
 * tokens.css — the CSS variables drive every surface, these drive only
 * MUI's palette maths.
 */
export const paletteTokens = {
  dark: {
    inflow: '#2dd4a7',
    outflow: '#f0785e',
    invest: '#8b7cff',
    warn: '#ffbd66',
    danger: '#ff6b86',
    text: '#f1f5f9',
    textDim: '#9aa4b2',
    bg: '#0a0f16',
    paper: '#141c27',
  },
  light: {
    inflow: '#0f766e',
    outflow: '#c14a32',
    invest: '#4f46e5',
    warn: '#9a6510',
    danger: '#b3261e',
    text: '#14241f',
    textDim: '#5b665f',
    bg: '#f6f2e8',
    paper: '#fdfcf8',
  },
} as const;

/** Categorical series colours for charts, ordered for maximum separation. */
export const seriesColors = (mode: 'light' | 'dark') =>
  mode === 'dark'
    ? ['#2dd4a7', '#8b7cff', '#f0785e', '#ffbd66', '#4cc9f0', '#ff6b86', '#a3e635', '#f472b6']
    : ['#0f766e', '#4f46e5', '#c14a32', '#9a6510', '#0369a1', '#b3261e', '#4d7c0f', '#be185d'];
